/* eslint-disable camelcase */
import { useUser } from 'controllers/contexts/user';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { MessageDetailsPresetId } from './presets';
import { isValidMessageDetails } from './schema';
import {
  FORCE_OVERRIDE_PARAM,
  messageDetailsStorageKey,
  MESSAGE_DETAILS_OVERRIDE_EVENT,
} from './storageKey';
import { MessageDetails } from './types';

export interface OverrideEntry {
  preset: MessageDetailsPresetId | 'custom';
  payload: MessageDetails;
}

const isOverrideEntry = (value: unknown): value is OverrideEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<OverrideEntry>;
  if (
    entry.preset !== 'A' &&
    entry.preset !== 'B' &&
    entry.preset !== 'C' &&
    entry.preset !== 'custom'
  ) {
    return false;
  }
  if (!entry.payload || !isValidMessageDetails(entry.payload)) return false;
  return true;
};

const readStored = (key: string): OverrideEntry | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isOverrideEntry(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const dispatchOverrideChange = (key: string): void => {
  window.dispatchEvent(
    new CustomEvent(MESSAGE_DETAILS_OVERRIDE_EVENT, { detail: { key } }),
  );
};

export interface UseMessageDetailsOverrideResult {
  override: OverrideEntry | null;
  setOverride: (
    preset: OverrideEntry['preset'],
    payload: MessageDetails,
  ) => void;
  clearOverride: () => void;
  // Derived from `?messageDetailsForceOverride=1` in the URL. Lets an admin
  // share a link that forces the override to win even when live data exists.
  isForced: boolean;
}

const NOOP_RESULT: UseMessageDetailsOverrideResult = {
  override: null,
  setOverride: () => undefined,
  clearOverride: () => undefined,
  isForced: false,
};

/**
 * Reads and writes a single per-shop Message Details override stored in
 * localStorage. Admin-gated: returns no-op values for non-admin users so
 * the override pathway is unreachable outside of PS staff design/dev work.
 */
export const useMessageDetailsOverride =
  (): UseMessageDetailsOverrideResult => {
    const { user } = useUser();
    const location = useLocation();

    const isAdmin = user?.is_admin === true;
    const shopId = typeof user?.shop_id === 'number' ? user.shop_id : undefined;

    const key = useMemo(
      () => (shopId !== undefined ? messageDetailsStorageKey(shopId) : null),
      [shopId],
    );

    const subscribe = useCallback(
      (onChange: () => void) => {
        const storageListener = (event: StorageEvent): void => {
          if (event.key === key) onChange();
        };
        const customListener = (event: Event): void => {
          const { detail } = event as CustomEvent<{ key: string }>;
          if (detail?.key === key) onChange();
        };
        window.addEventListener('storage', storageListener);
        window.addEventListener(MESSAGE_DETAILS_OVERRIDE_EVENT, customListener);
        return () => {
          window.removeEventListener('storage', storageListener);
          window.removeEventListener(
            MESSAGE_DETAILS_OVERRIDE_EVENT,
            customListener,
          );
        };
      },
      [key],
    );

    const getSnapshot = useCallback((): string | null => {
      if (!key) return null;
      return localStorage.getItem(key);
    }, [key]);

    // React 17 doesn't have useSyncExternalStore; this is the equivalent
    // useState/useEffect pattern. Storage events from other tabs and the
    // custom event from this tab both push a fresh snapshot into state.
    const [stored, setStored] = useState<string | null>(() => getSnapshot());
    useEffect(() => {
      setStored(getSnapshot());
      return subscribe(() => setStored(getSnapshot()));
    }, [subscribe, getSnapshot]);

    const override = useMemo<OverrideEntry | null>(() => {
      if (!isAdmin || !key || !stored) return null;
      return readStored(key);
    }, [isAdmin, key, stored]);

    const setOverride = useCallback<
      UseMessageDetailsOverrideResult['setOverride']
    >(
      (preset, payload) => {
        if (!isAdmin || !key) return;
        try {
          localStorage.setItem(key, JSON.stringify({ preset, payload }));
          dispatchOverrideChange(key);
        } catch {
          // Storage quota exceeded or other write failure. The editor
          // surfaces the toast; the hook stays a noisy no-op.
        }
      },
      [isAdmin, key],
    );

    const clearOverride = useCallback((): void => {
      if (!isAdmin || !key) return;
      try {
        localStorage.removeItem(key);
        dispatchOverrideChange(key);
      } catch {
        // ignore — there's no recoverable failure mode here
      }
    }, [isAdmin, key]);

    const isForced = useMemo(() => {
      if (!isAdmin) return false;
      return (
        new URLSearchParams(location.search).get(FORCE_OVERRIDE_PARAM) === '1'
      );
    }, [isAdmin, location.search]);

    if (!isAdmin) return NOOP_RESULT;

    return {
      override,
      setOverride,
      clearOverride,
      isForced,
    };
  };
